import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Input, Radio, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
    DatePicker as ZhDatePicker,
    Dialog as ZhDialog,
    Table as ZhTable,
    Upload as ZhUpload,
    type TableColumnProps,
    type UploadRequestOptions,
    type UploadUserFile
} from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import type { CustomerJsonInfo } from "@/entity/customerJsonInfo";
import { wxIdentitySelectedUrl } from "@/utils/config";
import { Permission } from "@/views/components/Permission";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { useUserStore } from "@/views/stores";
import { confirmDialog } from "@/utils/confirmDialog";
import { notifyOperationSuccess } from "@/utils/operationFeedback";

const PROMOTION_STATE = { VALID: 1, OVERDUE: 2, CLOSE: 3 } as const;
const stateLabels: Record<number, string> = { 1: "有效", 2: "过期", 3: "关闭" };
const levelLabels: Record<number, string> = { 1: "非会员", 2: "基础会员", 3: "高级会员" };

export interface PromotionRecord {
    id: number | string;
    code?: string;
    title?: string;
    customerInfo?: CustomerJsonInfo;
    hasGift?: number;
    level?: number;
    giftDuration?: number;
    redemptionValidity?: string;
    promotionValidity?: string;
    headPictureFileId?: number;
    paidCount?: number;
    unpaidCount?: number;
    createdTime?: string;
    state?: number;
}

export interface PromotionDetailRecord {
    detail?: { customerInfo?: CustomerJsonInfo; activationCode?: string };
    amount?: number;
}

export interface PromotionVo {
    promotion?: PromotionRecord;
    details?: { list?: PromotionDetailRecord[]; total?: number };
}

export interface PromotionQuery {
    page: number;
    size: number;
    code?: string;
    account?: string;
    title?: string;
    promotionValidity?: string;
    state?: string | number;
    sortKey?: string;
    sortOrder?: string;
}

export interface PromotionPageProps {
    loadPromotions?: (query: PromotionQuery) => Promise<{ list?: PromotionRecord[]; total?: number }>;
    closePromotion?: (id: number | string) => Promise<unknown>;
    delayPromotion?: (id: number | string, promotionValidity: string) => Promise<unknown>;
}

export interface PromotionFormPageProps {
    create?: (payload: Record<string, unknown>) => Promise<unknown>;
    loadCustomers?: (keyword: string) => Promise<CustomerJsonInfo[]>;
    uploadFile?: (file: File) => Promise<{ id?: number; path?: string }>;
}

export interface PromotionDetailPageProps {
    loadPromotion?: (id: number | string) => Promise<PromotionVo>;
    closePromotion?: (id: number | string) => Promise<unknown>;
    delayPromotion?: (id: number | string, promotionValidity: string) => Promise<unknown>;
    loadPromotionFile?: (id: number) => Promise<{ path?: string }>;
    loadTotalPrice?: (customerId: number, code: string) => Promise<number | string | undefined>;
}

const formatDateOnly = (value?: string) => (value ? value.slice(0, 10) : DEFAULT_VALUE);
const formatDateTime = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);
const customerLabel = (customer?: CustomerJsonInfo) => {
    if (!customer) return DEFAULT_VALUE;
    return (
        [customer.institutionName || customer.realName, customer.mobile, customer.code].filter(Boolean).join(" | ") ||
        DEFAULT_VALUE
    );
};

/** 复用 Vue `/admin/promotion/paging` 推广分页接口。 */
export async function loadPromotions(query: PromotionQuery) {
    const response = await request.service.get("/admin/promotion/paging", { params: query });
    return response.data as { list?: PromotionRecord[]; total?: number };
}

/** 复用 Vue 推广关闭接口。 */
export async function closePromotion(id: number | string) {
    const response = await request.service.put(`/admin/promotion/close/${id}`);
    return response.data;
}

/** 复用 Vue 推广延期接口及 query-string 参数契约。 */
export async function delayPromotion(id: number | string, promotionValidity: string) {
    const response = await request.service.put(`/admin/promotion/delay/${id}?promotionValidity=${promotionValidity}`);
    return response.data;
}

/** 复用 Vue 新增推广接口。 */
export async function createPromotion(payload: Record<string, unknown>) {
    const response = await request.service.post("/admin/promotion", payload);
    return response.data;
}

/** 复用 Vue 推广详情接口。 */
export async function loadPromotion(id: number | string) {
    const response = await request.service.get(`/admin/promotion/vo/${id}`);
    return response.data as PromotionVo;
}

/** 复用 Vue 推广详情头图文件查询接口。 */
export async function loadPromotionFile(id: number) {
    const response = await request.service.get(`/dataService/admin/file/${id}`);
    return response.data as { path?: string };
}

/** 复用 Vue 推广详情会员充值总额接口。 */
export async function loadPromotionTotalPrice(customerId: number, code: string) {
    const response = await request.service.get(`/admin/promotion/total/recharge/${customerId}?promotionCode=${code}`);
    return response.data as number | string | undefined;
}

/** 复用 Vue 新增推广的客户远程搜索接口。 */
export async function loadPromotionCustomers(keyword: string) {
    const response = await request.service.get("/admin/customer/selector", { params: { param: keyword } });
    return response.data as CustomerJsonInfo[];
}

/** 复用 Vue 推广图上传接口，页面只保留文件 ID 和预览地址。 */
export async function uploadPromotionFile(file: File): Promise<{ id?: number; path?: string }> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await request.service.post("/dataService/admin/file/upload", formData, { timeout: 0 });
    return response.data;
}

/** 推广日期沿用旧 Vue 规则，只允许选择明天及以后。 */
const isPromotionDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date.getTime() < today.getTime() + 24 * 60 * 60 * 1000;
};

function DelayModal({
    open,
    onCancel,
    onSubmit,
    submitting = false
}: {
    open: boolean;
    onCancel: () => void;
    onSubmit: (date: string) => Promise<void>;
    submitting?: boolean;
}) {
    const [date, setDate] = useState("");
    return (
        <ZhDialog
            open={open}
            title="延期"
            width={520}
            closeOnClickModal={false}
            closeOnPressEscape={false}
            onCancel={() => {
                setDate("");
                onCancel();
            }}
            footer={
                <Space>
                    <Button onClick={onCancel}>返回</Button>
                    <Button type="primary" loading={submitting} onClick={() => void onSubmit(date)}>
                        提交
                    </Button>
                </Space>
            }
        >
            <div>
                <span>推广有效期</span>
                <ZhDatePicker
                    type="date"
                    format="YYYY-MM-DD"
                    valueFormat="YYYY-MM-DD"
                    placeholder="请选择日期"
                    value={date}
                    disabledDate={isPromotionDateDisabled}
                    onChange={(_value, dateString) => setDate(typeof dateString === "string" ? dateString : "")}
                />
                <input
                    aria-label="推广有效期"
                    className="sr-only"
                    type="text"
                    value={date}
                    onChange={event => setDate(event.target.value)}
                />
            </div>
        </ZhDialog>
    );
}

export function PromotionPage({
    loadPromotions: load = loadPromotions,
    closePromotion: close = closePromotion,
    delayPromotion: delay = delayPromotion
}: PromotionPageProps) {
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const [records, setRecords] = useState<PromotionRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<PromotionQuery>({ page: 1, size: 10 });
    const [filters, setFilters] = useState({ code: "", account: "", title: "", promotionValidity: "", state: "" });
    const [errorMessage, setErrorMessage] = useState("");
    const [delayId, setDelayId] = useState<number | string>();
    const [delayOpen, setDelayOpen] = useState(false);

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (disposed) return;
                setRecords(result.list || []);
                setTotal(result.total || 0);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("推广列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [load, query]);

    /** 旧 Vue ProTable 会移除空筛选值，并在搜索时保留用户当前选择的分页条数。 */
    const submitSearch = () =>
        setQuery({
            page: 1,
            size: query.size,
            ...(filters.code ? { code: filters.code } : {}),
            ...(filters.account ? { account: filters.account } : {}),
            ...(filters.title ? { title: filters.title } : {}),
            ...(filters.promotionValidity ? { promotionValidity: filters.promotionValidity } : {}),
            ...(filters.state ? { state: filters.state } : {})
        });
    const closeRow = async (id: number | string) => {
        if (!(await confirmDialog("关闭后，通过该页面的注册，不会进入的推广有效记录里"))) return;
        try {
            await close(id);
            setQuery(current => ({ ...current }));
        } catch {
            setErrorMessage("推广关闭失败");
        }
    };
    const submitDelay = async (date: string) => {
        if (!date || delayId === undefined) {
            setErrorMessage("请选择推广有效期");
            return;
        }
        try {
            await delay(delayId, date);
            setDelayOpen(false);
            setDelayId(undefined);
            setQuery(current => ({ ...current }));
        } catch {
            setErrorMessage("推广延期失败");
        }
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 推广列表的字段、详情跳转和操作权限。
    const columns: TableColumnProps<PromotionRecord>[] = [
        {
            key: "code",
            label: "编号",
            width: 138,
            prop: "code",
            render: ({ row, cellValue }) => {
                const code = typeof cellValue === "string" || typeof cellValue === "number" ? cellValue : row.code;
                return (
                    <Button
                        type="link"
                        onClick={() =>
                            navigate(
                                `/lianYunUser/promotion/detail/${row.id}?customerId=${row.customerInfo?.id || ""}&code=${code || ""}`
                            )
                        }
                    >
                        {code || DEFAULT_VALUE}
                    </Button>
                );
            }
        },
        { key: "title", label: "标题", minWidth: 228, prop: "title" },
        {
            key: "account",
            label: "账号",
            minWidth: 228,
            prop: "customerInfo",
            render: ({ row }) => customerLabel(row.customerInfo)
        },
        {
            key: "paidCount",
            label: "已付费/未付费(个)",
            width: 150,
            prop: "paidCount",
            render: ({ row }) => `${row.paidCount || 0} / ${row.unpaidCount || 0}`
        },
        {
            key: "promotionValidity",
            label: "推广有效期",
            minWidth: 180,
            prop: "promotionValidity",
            render: ({ row }) => formatDateOnly(row.promotionValidity)
        },
        {
            key: "createdTime",
            label: "创建时间",
            minWidth: 180,
            prop: "createdTime",
            render: ({ row }) => formatDateTime(row.createdTime)
        },
        {
            key: "state",
            label: "状态",
            minWidth: 148,
            prop: "state",
            render: ({ row }) => stateLabels[Number(row.state)] || DEFAULT_VALUE
        },
        {
            key: "operation",
            label: "操作",
            width: 200,
            prop: "state",
            fixed: "right",
            render: ({ row }) =>
                Number(row.state) !== PROMOTION_STATE.CLOSE ? (
                    <Permission anyOf={["ROLE_PROMO_W"]}>
                        <Space>
                            <Button
                                aria-label="延期"
                                disabled={banButton}
                                type="link"
                                onClick={() => {
                                    setDelayId(row.id);
                                    setDelayOpen(true);
                                }}
                            >
                                延期
                            </Button>
                            <Button
                                aria-label="关闭"
                                disabled={banButton}
                                type="link"
                                onClick={() => void closeRow(row.id)}
                            >
                                关闭
                            </Button>
                        </Space>
                    </Permission>
                ) : null
        }
    ];

    return (
        <section aria-label="promotion-page" className="flex h-full min-h-full flex-col">
            <Card>
                <ZhTable<PromotionRecord>
                    data={records}
                    columns={normalizeOperationColumns(columns, records)}
                    pagination={false}
                    rowKey="id"
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title level={4}>推广管理</Typography.Title>

                            {errorMessage ? <Alert role="alert" message={errorMessage} type="error" /> : null}

                            <Space wrap className="mb-4 mt-4">
                                <Permission anyOf={["ROLE_PROMO_W"]}>
                                    <Button
                                        aria-label="新增推广"
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => navigate("/lianYunUser/promotion/add")}
                                    >
                                        新增
                                    </Button>
                                </Permission>
                                <Input
                                    aria-label="推广编号"
                                    maxLength={50}
                                    placeholder="请输入编号搜索"
                                    value={filters.code}
                                    onChange={event =>
                                        setFilters(current => ({ ...current, code: event.target.value }))
                                    }
                                />
                                <Input
                                    aria-label="推广账号"
                                    maxLength={50}
                                    placeholder="请输入账号信息搜索"
                                    value={filters.account}
                                    onChange={event =>
                                        setFilters(current => ({ ...current, account: event.target.value }))
                                    }
                                />
                                <Input
                                    aria-label="推广标题"
                                    placeholder="请输入标题搜索"
                                    value={filters.title}
                                    onChange={event =>
                                        setFilters(current => ({ ...current, title: event.target.value }))
                                    }
                                />
                                <ZhDatePicker
                                    aria-label="推广有效期"
                                    type="date"
                                    format="YYYY-MM-DD"
                                    placeholder="请选择推广有效期"
                                    valueFormat="YYYY-MM-DD"
                                    value={filters.promotionValidity}
                                    onChange={(_value, dateString) =>
                                        setFilters(current => ({
                                            ...current,
                                            promotionValidity: typeof dateString === "string" ? dateString : ""
                                        }))
                                    }
                                />
                                <Select
                                    allowClear
                                    aria-label="推广状态"
                                    options={Object.entries(stateLabels).map(([value, label]) => ({ label, value }))}
                                    placeholder="请选择状态"
                                    value={filters.state || undefined}
                                    onChange={value => setFilters(current => ({ ...current, state: value || "" }))}
                                />
                                <Button
                                    aria-label="查询推广"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                    onClick={submitSearch}
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置推广"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    title="重置"
                                    onClick={() => {
                                        setFilters({
                                            code: "",
                                            account: "",
                                            title: "",
                                            promotionValidity: "",
                                            state: ""
                                        });
                                        setQuery(current => ({ page: 1, size: current.size }));
                                    }}
                                />
                            </Space>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        // 切换条数后回到第一页，沿用 Vue ProTable 的分页请求契约。
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <DelayModal open={delayOpen} onCancel={() => setDelayOpen(false)} onSubmit={submitDelay} />
        </section>
    );
}

export function PromotionFormPage({
    create = createPromotion,
    loadCustomers = loadPromotionCustomers,
    uploadFile = uploadPromotionFile
}: PromotionFormPageProps) {
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const [form, setForm] = useState({
        title: "",
        customerId: "",
        hasGift: 1,
        level: 2,
        giftDuration: "",
        redemptionValidity: "",
        promotionValidity: "",
        headPictureFileId: ""
    });
    const [customers, setCustomers] = useState<CustomerJsonInfo[]>([]);
    const [headPictureUploadList, setHeadPictureUploadList] = useState<UploadUserFile[]>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const customerSearchRequestRef = useRef(0);
    const customerSearchKeywordRef = useRef("");
    const update = (key: string, value: string | number) => setForm(current => ({ ...current, [key]: value }));
    const selectedCustomer = useMemo(
        () => customers.find(customer => String(customer.id) === form.customerId),
        [customers, form.customerId]
    );
    const submit = async () => {
        if (!form.title || !selectedCustomer || !form.promotionValidity || !form.headPictureFileId) {
            setErrorMessage("请完善推广信息");
            return;
        }
        try {
            await create({
                title: form.title,
                hasGift: form.hasGift,
                level: form.hasGift === 1 ? Number(form.level) : 1,
                giftDuration: form.hasGift === 1 ? Number(form.giftDuration) : 0,
                redemptionValidity: form.hasGift === 1 ? form.redemptionValidity : "",
                promotionValidity: form.promotionValidity,
                headPictureFileId: Number(form.headPictureFileId),
                customerInfo: selectedCustomer,
                // 保留旧 Vue 新增推广接口用于生成小程序推广页的页面标识。
                page: wxIdentitySelectedUrl
            });
            notifyOperationSuccess("新增推广成功");
            navigate(-1);
        } catch {
            setErrorMessage("新增推广失败");
        }
    };
    const searchCustomers = async (keyword: string) => {
        const normalizedKeyword = keyword.trim();
        customerSearchKeywordRef.current = normalizedKeyword;
        if (normalizedKeyword) update("customerId", "");
        const requestId = ++customerSearchRequestRef.current;
        try {
            const options = await loadCustomers(normalizedKeyword);
            if (requestId === customerSearchRequestRef.current) setCustomers(options);
        } catch {
            if (requestId !== customerSearchRequestRef.current) return;
            setErrorMessage("关联账号加载失败");
        }
    };
    // 对齐旧 Vue UploadList 的单图、JPG/PNG、2MB 限制。
    const beforeHeadPictureUpload = (file: File) => {
        if (file.type !== "image/jpeg" && file.type !== "image/png") {
            setErrorMessage("仅支持 JPG、PNG 图片");
            return false;
        }
        if (file.size > 2 * 1024 * 1024) {
            setErrorMessage("推广图片大小不能超过2MB");
            return false;
        }
        return true;
    };
    // 上传成功后把文件 ID 写入 Vue 兼容的新增推广 payload。
    const uploadHeadPicture = async (options: UploadRequestOptions) => {
        try {
            const uploaded = await uploadFile(options.file);
            const fileId = Number(uploaded.id);
            if (!fileId) throw new Error("missing file id");
            setForm(current => ({ ...current, headPictureFileId: String(fileId) }));
            setHeadPictureUploadList([
                { name: options.file.name, status: "success", uid: Date.now(), url: uploaded.path }
            ]);
            options.onSuccess(uploaded);
            setErrorMessage("");
        } catch (error) {
            options.onError(error instanceof Error ? error : new Error("upload failed"));
        }
    };
    return (
        <section aria-label="promotion-form-page" className="flex h-full min-h-full flex-col">
            <Card>
                <Typography.Title level={4}>新增推广</Typography.Title>
                {errorMessage ? <Alert role="alert" message={errorMessage} type="error" /> : null}
                <Space direction="vertical" className="w-full">
                    <label>
                        标题
                        <Input
                            aria-label="推广标题"
                            value={form.title}
                            onChange={event => update("title", event.target.value)}
                        />
                    </label>
                    <label>
                        关联账号
                        <Select
                            allowClear
                            showSearch
                            aria-label="关联账号"
                            filterOption={false}
                            options={customers.map(customer => ({
                                label: customerLabel(customer),
                                value: String(customer.id)
                            }))}
                            placeholder="请输入账号信息搜索"
                            value={form.customerId || undefined}
                            onChange={value => update("customerId", value || "")}
                            onSearch={keyword => void searchCustomers(keyword)}
                            onOpenChange={open => {
                                if (open && !customers.length && !customerSearchKeywordRef.current) {
                                    void searchCustomers("");
                                }
                            }}
                        />
                    </label>
                    <label>
                        是否赠送会员
                        <Radio.Group
                            aria-label="是否赠送会员"
                            options={[
                                { label: "是", value: 1 },
                                { label: "否", value: 2 }
                            ]}
                            value={form.hasGift}
                            onChange={event => update("hasGift", Number(event.target.value))}
                        />
                    </label>
                    {form.hasGift === 1 ? (
                        <>
                            <label>
                                赠送会员类型
                                <Radio.Group
                                    aria-label="赠送会员类型"
                                    options={[
                                        { label: "基础会员", value: 2 },
                                        { label: "高级会员", value: 3 }
                                    ]}
                                    value={form.level}
                                    onChange={event => update("level", Number(event.target.value))}
                                />
                            </label>
                            <label>
                                赠送时长
                                <Input
                                    aria-label="赠送时长"
                                    value={form.giftDuration}
                                    onChange={event => update("giftDuration", event.target.value)}
                                />
                            </label>
                            <div>
                                <span>兑换有效期</span>
                                <ZhDatePicker
                                    type="date"
                                    format="YYYY-MM-DD"
                                    valueFormat="YYYY-MM-DD"
                                    value={form.redemptionValidity}
                                    disabledDate={isPromotionDateDisabled}
                                    onChange={(_value, dateString) =>
                                        update("redemptionValidity", typeof dateString === "string" ? dateString : "")
                                    }
                                />
                                <input
                                    aria-label="兑换有效期"
                                    className="sr-only"
                                    type="text"
                                    value={form.redemptionValidity}
                                    onChange={event => update("redemptionValidity", event.target.value)}
                                />
                            </div>
                        </>
                    ) : null}
                    <div>
                        <span>推广有效期</span>
                        <ZhDatePicker
                            type="date"
                            format="YYYY-MM-DD"
                            valueFormat="YYYY-MM-DD"
                            value={form.promotionValidity}
                            disabledDate={isPromotionDateDisabled}
                            onChange={(_value, dateString) =>
                                update("promotionValidity", typeof dateString === "string" ? dateString : "")
                            }
                        />
                        <input
                            aria-label="推广有效期"
                            className="sr-only"
                            type="text"
                            value={form.promotionValidity}
                            onChange={event => update("promotionValidity", event.target.value)}
                        />
                    </div>
                    <label>
                        推广头像
                        <ZhUpload
                            action=""
                            accept="image/jpeg,image/png"
                            className="promotion-head-picture-upload"
                            fileList={headPictureUploadList}
                            limit={1}
                            beforeUpload={beforeHeadPictureUpload}
                            httpRequest={uploadHeadPicture}
                        />
                        <input
                            aria-label="推广头像文件 ID"
                            className="sr-only"
                            value={form.headPictureFileId}
                            onChange={event => update("headPictureFileId", event.target.value)}
                        />
                    </label>
                    <Space>
                        <Button aria-label="保存推广" type="primary" disabled={banButton} onClick={() => void submit()}>
                            保存
                        </Button>
                        <Button aria-label="返回推广列表" onClick={() => navigate(-1)}>
                            返回
                        </Button>
                    </Space>
                </Space>
            </Card>
        </section>
    );
}

export function PromotionDetailPage({
    loadPromotion: load = loadPromotion,
    closePromotion: close = closePromotion,
    delayPromotion: delay = delayPromotion,
    loadPromotionFile: loadFile = loadPromotionFile,
    loadTotalPrice = loadPromotionTotalPrice
}: PromotionDetailPageProps) {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const [data, setData] = useState<PromotionVo>({});
    const [errorMessage, setErrorMessage] = useState("");
    const [delayOpen, setDelayOpen] = useState(false);
    const [headPictureUrl, setHeadPictureUrl] = useState("");
    const [rechargeAmount, setRechargeAmount] = useState<number | string>();
    useEffect(() => {
        if (!id) return;
        let disposed = false;
        void load(id)
            .then(result => {
                if (disposed) return;
                setData(result);
                const currentPromotion = result.promotion;
                if (currentPromotion?.headPictureFileId != null) {
                    void loadFile(currentPromotion.headPictureFileId)
                        .then(file => {
                            if (!disposed) setHeadPictureUrl(file.path || "");
                        })
                        .catch(() => undefined);
                }
                const customerId = currentPromotion?.customerInfo?.id;
                const promotionCode = currentPromotion?.code || searchParams.get("code");
                if (customerId != null && promotionCode) {
                    void loadTotalPrice(customerId, promotionCode)
                        .then(amount => {
                            if (!disposed) setRechargeAmount(amount);
                        })
                        .catch(() => undefined);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("推广详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, load, loadFile, loadTotalPrice, searchParams]);
    const promotion = data.promotion;
    const closeDetail = async () => {
        if (!id || !(await confirmDialog("关闭后，通过该页面的注册，不会进入的推广有效记录里"))) return;
        try {
            await close(id);
            notifyOperationSuccess("操作成功");
            setData(current => ({
                ...current,
                promotion: current.promotion ? { ...current.promotion, state: PROMOTION_STATE.CLOSE } : undefined
            }));
        } catch {
            setErrorMessage("推广关闭失败");
        }
    };
    const submitDelay = async (date: string) => {
        if (!id || !date) {
            setErrorMessage("请选择推广有效期");
            return;
        }
        try {
            await delay(id, date);
            notifyOperationSuccess("操作成功");
            setDelayOpen(false);
        } catch {
            setErrorMessage("推广延期失败");
        }
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 推广详情中的有效记录字段。
    const detailColumns: TableColumnProps<PromotionDetailRecord>[] = [
        {
            key: "customerId",
            label: "用户 ID",
            width: 138,
            prop: "detail",
            render: ({ row }) => row.detail?.customerInfo?.id || DEFAULT_VALUE
        },
        {
            key: "customerName",
            label: "个人实名/机构名称",
            minWidth: 150,
            prop: "detail",
            render: ({ row }) => customerLabel(row.detail?.customerInfo)
        },
        {
            key: "mobile",
            label: "注册手机号",
            width: 180,
            prop: "detail",
            render: ({ row }) => row.detail?.customerInfo?.mobile || DEFAULT_VALUE
        },
        {
            key: "activationCode",
            label: "激活码",
            width: 158,
            prop: "detail",
            render: ({ row }) => row.detail?.activationCode || DEFAULT_VALUE
        },
        { key: "amount", label: "会员充值金额", width: 158, prop: "amount", render: ({ row }) => row.amount ?? 0 }
    ];

    return (
        <section aria-label="promotion-detail-page" className="flex h-full min-h-full flex-col">
            <Card>
                <Space direction="vertical" className="w-full">
                    <Typography.Title level={4}>推广详情</Typography.Title>
                    {errorMessage ? <Alert role="alert" message={errorMessage} type="error" /> : null}
                    <div>创建时间：{formatDateTime(promotion?.createdTime)}</div>
                    <div>状态：{stateLabels[Number(promotion?.state)] || DEFAULT_VALUE}</div>
                    <div>推广编号：{promotion?.code || searchParams.get("code") || DEFAULT_VALUE}</div>
                    <div>标题：{promotion?.title || DEFAULT_VALUE}</div>
                    <div>关联账号：{customerLabel(promotion?.customerInfo)}</div>
                    {promotion?.level == null || promotion.level !== 1 ? (
                        <>
                            <div>赠送会员：{levelLabels[Number(promotion?.level)] || DEFAULT_VALUE}</div>
                            <div>
                                赠送时长：{promotion?.giftDuration ? `${promotion.giftDuration}天` : DEFAULT_VALUE}
                            </div>
                            <div>兑换有效期：{formatDateOnly(promotion?.redemptionValidity)}</div>
                            <div>推广有效期：{formatDateOnly(promotion?.promotionValidity)}</div>
                        </>
                    ) : null}
                    <div>推广头图：{headPictureUrl ? <img alt="推广头图" src={headPictureUrl} /> : DEFAULT_VALUE}</div>
                    <div>推广有效记录</div>
                    <div>
                        已付费/未付费：
                        {promotion ? `${promotion.paidCount ?? 0}/${promotion.unpaidCount ?? 0}个` : DEFAULT_VALUE}
                    </div>
                    <div>合计会员充值金额：{rechargeAmount == null ? DEFAULT_VALUE : `${rechargeAmount}元`}</div>
                    <ZhTable<PromotionDetailRecord>
                        data={data.details?.list || []}
                        columns={normalizeOperationColumns(detailColumns, data.details?.list || [])}
                        pagination={false}
                        rowKey={row => String(row.detail?.customerInfo?.id || row.detail?.activationCode || "")}
                    />
                    <Space>
                        <Permission anyOf={["ROLE_PROMO_W"]}>
                            <Button
                                aria-label="详情延期"
                                disabled={banButton || Number(promotion?.state) === PROMOTION_STATE.CLOSE}
                                onClick={() => setDelayOpen(true)}
                            >
                                延期
                            </Button>
                            <Button
                                aria-label="详情关闭"
                                disabled={banButton || Number(promotion?.state) === PROMOTION_STATE.CLOSE}
                                onClick={() => void closeDetail()}
                            >
                                关闭
                            </Button>
                        </Permission>
                        <Button aria-label="返回推广列表" onClick={() => navigate(-1)}>
                            返回
                        </Button>
                    </Space>
                </Space>
            </Card>
            <DelayModal open={delayOpen} onCancel={() => setDelayOpen(false)} onSubmit={submitDelay} />
        </section>
    );
}
